<script setup lang="ts">
import { computed } from 'vue'

/**
 * One enterprise's slot use on the Staff Performance page: a bar per hour for the average number of its runs running
 * in that hour, under a dashed line at its concurrent-run limit. Drawn as plain SVG with the theme's chart tokens; the
 * label says the figures in words, so the colours never carry the meaning alone.
 */
const props = defineProps<{
    /** Average runs running in each hour, oldest first. */
    hourly: number[]
    limit: number
    /** Words for the window, such as "the last 24 hours". */
    windowWords: string
}>()

const WIDTH = 480
const HEIGHT = 64

const top = computed(() => Math.max(props.limit, ...props.hourly, 1))
const barWidth = computed(() => WIDTH / Math.max(props.hourly.length, 1))
const bars = computed(() =>
    props.hourly.map((value, index) => {
        const height = (value / top.value) * HEIGHT
        return { x: index * barWidth.value, y: HEIGHT - height, height, value }
    }),
)
const limitY = computed(() => HEIGHT - (props.limit / top.value) * HEIGHT)
const peak = computed(() => Math.max(0, ...props.hourly))
const label = computed(
    () =>
        `Runs running each hour over ${props.windowWords}: peak hour averaged ${peak.value.toFixed(1)} against a limit of ${props.limit}.`,
)
</script>

<template>
	<svg
		class="staff-slot-chart"
		:viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
		preserveAspectRatio="none"
		role="img"
		:aria-label="label"
		data-testid="staff-slot-chart"
	>
		<rect
			v-for="(bar, index) in bars"
			:key="index"
			:x="bar.x + barWidth * 0.1"
			:y="bar.y"
			:width="barWidth * 0.8"
			:height="bar.height"
			class="staff-slot-chart__bar"
			:data-over="bar.value >= limit && limit > 0 ? 'true' : undefined"
		>
			<title>{{ bar.value.toFixed(2) }} running</title>
		</rect>
		<line
			x1="0"
			:x2="WIDTH"
			:y1="limitY"
			:y2="limitY"
			class="staff-slot-chart__limit"
			vector-effect="non-scaling-stroke"
		/>
	</svg>
</template>

<style scoped>
.staff-slot-chart {
    display: block;
    width: 100%;
    height: 4rem;
    background: var(--muted);
    border: 1px solid var(--border);
}

.staff-slot-chart__bar {
    fill: var(--chart-2);
}

.staff-slot-chart__bar[data-over='true'] {
    fill: var(--chart-1);
}

.staff-slot-chart__limit {
    stroke: var(--chart-3);
    stroke-width: 1.5;
    stroke-dasharray: 4 3;
}
</style>
